import { useState } from "react"
import { SignInButton, UserButton, useAuth } from "@clerk/clerk-react"
import { ChevronDown, Plus, Save } from "lucide-react"
import AtlasMark from "@/components/AtlasMark"
import { useStudioStore } from "@/store/studio"
import { usd } from "@/lib/api"

export default function TopBar() {
  const { isSignedIn } = useAuth()
  const store = useStudioStore()
  const [menuOpen, setMenuOpen] = useState(false)
  const [title, setTitle] = useState(store.title)

  const handleSaveTitle = () => {
    store.updateTitle(title)
    window.dispatchEvent(new CustomEvent("atlas:toast", { detail: { message: "Title updated" } }))
  }

  return (
    <header className="h-14 border-b border-[#1e2130] bg-[#08090d]/90 backdrop-blur-md flex items-center justify-between gap-2 px-2.5 sm:px-4 sticky top-0 z-40">
      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
        <a href="/" className="flex items-center gap-2 group">
          <AtlasMark size={26} />
          <span className="font-semibold text-[#f0f1f5] tracking-tight hidden sm:block">
            Atlas <span className="text-[#9ca3af] font-normal text-xs ml-0.5">Studio</span>
          </span>
        </a>

        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-md bg-[#0f1117] border border-[#1e2130] hover:border-[#2d3148] transition-colors min-w-0"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#10b981]" />
            <div className="text-left">
              <p className="hidden sm:block text-[10px] text-[#6b7280] leading-none">Active production</p>
              <p className="text-xs font-medium text-[#f0f1f5] truncate max-w-[32vw] sm:max-w-[140px]">{store.title}</p>
            </div>
            <ChevronDown className="w-3 h-3 text-[#6b7280]" />
          </button>

          {menuOpen && (
            <div className="fixed sm:absolute top-16 sm:top-full left-2 right-2 sm:left-0 sm:right-auto sm:mt-2 sm:w-72 surface-elevated p-4 z-50">
              <label className="text-xs font-medium text-[#9ca3af] mb-1.5 block">Project title</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  className="input flex-1"
                />
                <button onClick={handleSaveTitle} className="btn-primary px-3">Save</button>
              </div>
              <button onClick={() => { store.resetProject(); setTitle("Untitled project"); setMenuOpen(false); window.location.assign("/studio") }} className="mt-3 w-full btn-subtle text-xs"><Plus className="w-3.5 h-3.5" /> New project</button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {store.identity && (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("atlas:open-credit-dialog", { detail: { message: `${usd(store.identity!.creditMicros)} is currently available for production.` } }))}
            className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0f1117] border border-[#1e2130] text-xs text-[#9ca3af] hover:text-[#f0f1f5] hover:border-[#363b50] transition-colors"
            aria-label={`${usd(store.identity.creditMicros)} production credit available. Add credit`}
          >
            {usd(store.identity.creditMicros)} credit
          </button>
        )}
        <button aria-label="Save project" className="hidden sm:block p-2 rounded-md hover:bg-[#1e2130] transition-colors text-[#9ca3af]">
          <Save className="w-4 h-4" />
        </button>
        {isSignedIn ? (
          <UserButton afterSignOutUrl="/" appearance={{ elements: { avatarBox: "w-7 h-7" } }} />
        ) : (
          <SignInButton mode="modal" fallbackRedirectUrl={window.location.pathname}>
            <button className="btn-subtle text-xs">Sign in</button>
          </SignInButton>
        )}
      </div>
    </header>
  )
}
